// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import { createReference, getReferenceString } from '@medplum/core';
import type { Appointment, Coding, Practitioner, Resource, ResourceType, Schedule, Slot } from '@medplum/fhirtypes';
import { getTelehealthInfo } from '@polarissleep/scheduling';
import { type ProposedAppointment, SCHEDULING_V2_PROVENANCE_TAG } from '@polarissleep/scheduling-next';
import { getProviderAppEnvConfig } from '../../config/env';
import { botIdentifier } from '../../lib/bot-identifier';
import { getOrganizationId, withTenantScope } from '../../utils/tenant';
import { buildIntakeQuestionnaireResponse, generateTestIdentity } from './zoomIntegrationFixtures';

export const TEST_TAG_SYSTEM = 'http://polarissleep.com/test';
export const TEST_TAG_CODE = 'zoom-integration-test';
export const TEST_TAG: Coding = { system: TEST_TAG_SYSTEM, code: TEST_TAG_CODE };

const POLL_INTERVAL_MS = 1000;
const POLL_MAX_ATTEMPTS = 30;
const SLOT_LEAD_MINUTES = 1;
const SLOT_DURATION_MINUTES = 30;

type IntakeBookingResult = {
  appointmentId: string;
  encounterId: string;
  slotId?: string;
};

export interface StepLogEntry {
  level: 'info' | 'success' | 'error';
  message: string;
}

export type StepLogger = (entry: StepLogEntry) => void;

export interface RunOnboardingChainResult {
  uniqueId: string;
  patientId: string;
  appointmentId: string;
  encounterId: string;
  slotId: string;
  zoomJoinUrl: string;
}

interface IntakeAppointmentCandidate {
  appointment: ProposedAppointment;
}

interface FindIntakeSlotsResult {
  appointments?: IntakeAppointmentCandidate[];
}

/**
 * Drives the pre-auth onboarding chain from an admin context. Legacy mode seeds
 * a free Slot; v2 mode finds and books a proposed Appointment through the
 * intake bots. Both paths poll the resulting Appointment until the
 * `create-zoom-meetings` subscription bot writes the telehealth extension.
 */
export async function runOnboardingChain(medplum: MedplumClient, log: StepLogger): Promise<RunOnboardingChainResult> {
  const profile = medplum.getProfile();
  if (!profile || profile.resourceType !== 'Practitioner' || !profile.id) {
    throw new Error('You must be signed in as a Practitioner to run this test.');
  }
  const practitioner = profile as Practitioner & { id: string };
  const schedulingEngine = getProviderAppEnvConfig().schedulingEngine;

  const identity = generateTestIdentity();
  log({ level: 'info', message: `Test identity: ${identity.email}` });

  // Use the current user's own compartment org for the test data. Falling back
  // to VITE_MEDPLUM_ORGANIZATION_ID risks landing the Patient in a compartment
  // the caller's AccessPolicy doesn't include (the bot creates with admin
  // privileges; the subsequent client read uses the user's session and 404s).
  const orgIdForTest = extractOrgIdFromProfile(practitioner) ?? getOrganizationId();
  if (!orgIdForTest) {
    throw new Error('Could not resolve tenant Organization id from your profile or env.');
  }
  log({ level: 'info', message: `Using tenant org: Organization/${orgIdForTest}` });

  let seededSlot: (Slot & { id: string; start: string; end: string }) | undefined;
  if (schedulingEngine === 'legacy') {
    log({ level: 'info', message: 'Ensuring Schedule exists for current practitioner...' });
    const schedule = await ensureScheduleForPractitioner(medplum, practitioner);
    log({ level: 'success', message: `Schedule ${schedule.id}` });

    log({ level: 'info', message: `Seeding test Slot (+${SLOT_LEAD_MINUTES} min)...` });
    seededSlot = await seedTestSlot(medplum, schedule);
    log({ level: 'success', message: `Slot ${seededSlot.id} at ${seededSlot.start}` });
  } else {
    log({ level: 'info', message: 'Using Medplum v2 `$find`/`$book`; no free Slot will be seeded.' });
  }

  log({ level: 'info', message: 'Submitting intake form (process-intake-form bot)...' });
  const intakeResult = (await medplum.executeBot(
    botIdentifier('process-intake-form'),
    {
      questionnaireResponse: buildIntakeQuestionnaireResponse(identity),
      organizationId: orgIdForTest,
    },
    'application/json'
  )) as { eligible: boolean; uniqueId?: string; patientId?: string; reason?: string };

  if (!intakeResult.eligible || !intakeResult.patientId || !intakeResult.uniqueId) {
    throw new Error(`Intake form rejected: ${intakeResult.reason ?? 'unknown reason'}`);
  }
  log({
    level: 'success',
    message: `Patient ${intakeResult.patientId} created (onboarding id ${intakeResult.uniqueId})`,
  });

  log({ level: 'info', message: 'Tagging intake-created resources for cleanup...' });
  await tagIntakeResources(medplum, intakeResult.patientId, log);

  log({ level: 'info', message: 'Choosing appointment branch on eligibility...' });
  await medplum.executeBot(
    botIdentifier('process-eligibility-step'),
    {
      uniqueId: intakeResult.uniqueId,
      phase: 'eligibility-confirmation',
      eligibilityChoice: 'appointment',
    },
    'application/json'
  );
  log({ level: 'success', message: 'Patient advanced to eligibility-appointment-pending' });

  log({ level: 'info', message: 'Booking intake appointment (book-intake-slot bot)...' });
  const bookResult =
    schedulingEngine === 'legacy'
      ? ((await medplum.executeBot(
          botIdentifier('book-intake-slot'),
          {
            uniqueId: intakeResult.uniqueId,
            slotId: seededSlot?.id,
            start: seededSlot?.start,
            end: seededSlot?.end,
            providerId: practitioner.id,
          },
          'application/json'
        )) as IntakeBookingResult)
      : await bookV2IntakeAppointment(medplum, intakeResult.uniqueId, log);
  log({ level: 'success', message: `Appointment ${bookResult.appointmentId} + Encounter ${bookResult.encounterId}` });

  const slotId = bookResult.slotId ?? seededSlot?.id;

  await tagResourcesByRef(
    medplum,
    [
      { resourceType: 'Appointment', id: bookResult.appointmentId },
      { resourceType: 'Encounter', id: bookResult.encounterId },
      ...(slotId ? [{ resourceType: 'Slot' as const, id: slotId }] : []),
    ],
    log
  );

  log({ level: 'info', message: 'Waiting for create-zoom-meetings subscription bot...' });
  const zoomJoinUrl = await pollForZoomLink(medplum, bookResult.appointmentId, orgIdForTest, log);
  log({ level: 'success', message: 'Zoom link ready' });

  return {
    uniqueId: intakeResult.uniqueId,
    patientId: intakeResult.patientId,
    appointmentId: bookResult.appointmentId,
    encounterId: bookResult.encounterId,
    slotId: slotId ?? '',
    zoomJoinUrl,
  };
}

async function bookV2IntakeAppointment(
  medplum: MedplumClient,
  uniqueId: string,
  log: StepLogger
): Promise<{ appointmentId: string; encounterId: string; slotId?: string }> {
  const start = new Date(Date.now() + 24 * 60 * 60 * 1000);
  const end = new Date(start.getTime() + 14 * 24 * 60 * 60 * 1000);
  const findResult = (await medplum.executeBot(
    botIdentifier('find-intake-slots'),
    { uniqueId, fromDate: start.toISOString(), toDate: end.toISOString() },
    'application/json'
  )) as FindIntakeSlotsResult;
  const candidate = findResult.appointments?.[0];
  if (!candidate?.appointment) {
    throw new Error('Medplum v2 intake test found no proposed appointments in the next 14 days.');
  }
  log({ level: 'success', message: `Found proposed appointment at ${candidate.appointment.start}` });

  const result = (await medplum.executeBot(
    botIdentifier('book-intake-slot'),
    { uniqueId, appointment: candidate.appointment },
    'application/json'
  )) as IntakeBookingResult;
  const bookedAppointment = (await medplum.readResource('Appointment', result.appointmentId)) as Appointment;
  const slotReference = bookedAppointment.slot?.find((reference) =>
    reference.reference?.startsWith('Slot/')
  )?.reference;

  return {
    ...result,
    slotId: slotReference?.slice('Slot/'.length),
  };
}

async function ensureScheduleForPractitioner(
  medplum: MedplumClient,
  practitioner: Practitioner & { id: string }
): Promise<Schedule & { id: string }> {
  const existing = await medplum.searchOne('Schedule', { actor: getReferenceString(practitioner) });
  if (existing?.id) {
    return existing as Schedule & { id: string };
  }
  const created = await medplum.createResource<Schedule>(
    withTenantScope({
      resourceType: 'Schedule',
      actor: [createReference(practitioner)],
      active: true,
    })
  );
  if (!created.id) {
    throw new Error('Failed to create Schedule');
  }
  return created as Schedule & { id: string };
}

async function seedTestSlot(
  medplum: MedplumClient,
  schedule: Schedule & { id: string }
): Promise<Slot & { id: string; start: string; end: string }> {
  const start = new Date(Date.now() + SLOT_LEAD_MINUTES * 60 * 1000);
  const end = new Date(start.getTime() + SLOT_DURATION_MINUTES * 60 * 1000);
  const created = await medplum.createResource<Slot>(
    withTenantScope({
      resourceType: 'Slot',
      schedule: createReference(schedule),
      status: 'free',
      start: start.toISOString(),
      end: end.toISOString(),
      meta: { tag: [TEST_TAG, SCHEDULING_V2_PROVENANCE_TAG] },
    })
  );
  if (!created.id || !created.start || !created.end) {
    throw new Error('Failed to create Slot');
  }
  return created as Slot & { id: string; start: string; end: string };
}

/**
 * Extracts the tenant Organization id from the caller's profile's `meta.account`.
 * Returns undefined if the profile has no compartment account set (e.g., global
 * project admin with no AccessPolicy compartment).
 */
function extractOrgIdFromProfile(profile: Practitioner): string | undefined {
  const ref = profile.meta?.account?.reference;
  if (!ref?.startsWith('Organization/')) {
    return undefined;
  }
  return ref.slice('Organization/'.length);
}

/**
 * Tags the Patient itself plus any QR / Coverage / Consent / Condition that the
 * intake bot created in its Transaction Bundle. Best-effort: a tag failure on
 * any one resource (e.g., AccessPolicy filters it out) is logged but does not
 * abort the chain — the cleanup button will still find what was tagged, and
 * the Zoom test itself does not depend on tagging.
 */
async function tagIntakeResources(medplum: MedplumClient, patientId: string, log: StepLogger): Promise<void> {
  await tagResourceBestEffort(medplum, 'Patient', patientId, log);

  const subjectRef = `Patient/${patientId}`;
  const searches: { rt: ResourceType; param: string }[] = [
    { rt: 'QuestionnaireResponse', param: 'subject' },
    { rt: 'Coverage', param: 'beneficiary' },
    { rt: 'Consent', param: 'patient' },
    { rt: 'Condition', param: 'subject' },
  ];
  for (const { rt, param } of searches) {
    try {
      const found = await medplum.searchResources(rt, { [param]: subjectRef });
      for (const r of found) {
        if (r.id) {
          await tagResourceBestEffort(medplum, rt, r.id, log);
        }
      }
    } catch (err) {
      log({ level: 'info', message: `Skipped tagging ${rt} (search failed): ${errMsg(err)}` });
    }
  }
}

async function tagResourcesByRef(
  medplum: MedplumClient,
  refs: { resourceType: ResourceType; id: string }[],
  log: StepLogger
): Promise<void> {
  for (const ref of refs) {
    await tagResourceBestEffort(medplum, ref.resourceType, ref.id, log);
  }
}

async function tagResourceBestEffort(
  medplum: MedplumClient,
  resourceType: ResourceType,
  id: string,
  log: StepLogger
): Promise<void> {
  try {
    await tagResource(medplum, resourceType, id);
  } catch (err) {
    log({ level: 'info', message: `Skipped tagging ${resourceType}/${id}: ${errMsg(err)}` });
  }
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

async function tagResource(medplum: MedplumClient, resourceType: ResourceType, id: string): Promise<void> {
  const res = (await medplum.readResource(resourceType, id)) as Resource;
  const existing = res.meta?.tag ?? [];
  if (existing.some((t) => t.system === TEST_TAG_SYSTEM && t.code === TEST_TAG_CODE)) {
    return;
  }
  await medplum.updateResource({
    ...res,
    meta: { ...res.meta, tag: [...existing, TEST_TAG] },
  });
}

async function pollForZoomLink(
  medplum: MedplumClient,
  appointmentId: string,
  orgIdForTest: string,
  log: StepLogger
): Promise<string> {
  // The MedplumClient is initialized with cacheTime: 60000 (see provider-app
  // main.tsx), so repeated reads of the same id within the polling window
  // would otherwise return a stale pre-bot snapshot. Invalidate the URL
  // before each fetch so we always go to the server.
  const url = medplum.fhirUrl('Appointment', appointmentId);
  for (let attempt = 1; attempt <= POLL_MAX_ATTEMPTS; attempt++) {
    medplum.invalidateUrl(url);
    const appt = (await medplum.readResource('Appointment', appointmentId)) as Appointment;
    const info = getTelehealthInfo(appt);
    if (info) {
      return info.joinUrl;
    }
    if (attempt % 5 === 0) {
      log({ level: 'info', message: `Still polling (${attempt}/${POLL_MAX_ATTEMPTS})...` });
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
  const diagnostic = await diagnoseZoomLinkTimeout(medplum, appointmentId, orgIdForTest);
  throw new Error(diagnostic);
}

async function diagnoseZoomLinkTimeout(
  medplum: MedplumClient,
  appointmentId: string,
  orgIdForTest: string
): Promise<string> {
  const timeoutSeconds = (POLL_MAX_ATTEMPTS * POLL_INTERVAL_MS) / 1000;
  const lines = [`Timed out after ${timeoutSeconds}s waiting for Zoom link on Appointment/${appointmentId}.`];

  try {
    medplum.invalidateUrl(medplum.fhirUrl('Appointment', appointmentId));
    const appointment = (await medplum.readResource('Appointment', appointmentId)) as Appointment;
    const telehealthInfo = getTelehealthInfo(appointment);

    lines.push(
      `Appointment status: ${appointment.status ?? 'unknown'}${appointment.start ? `; start: ${appointment.start}` : ''}.`
    );

    if (telehealthInfo?.joinUrl) {
      lines.push('The appointment now has a Zoom link. Refresh and retry.');
      return lines.join(' ');
    }

    lines.push('The appointment exists but still has no telehealth extension.');
  } catch (err) {
    lines.push(`Could not re-read the appointment for diagnosis: ${errMsg(err)}.`);
    return lines.join(' ');
  }

  const zoomBotIdentifier = botIdentifier('create-zoom-meetings');
  try {
    const zoomBot = await medplum.searchOne('Bot', {
      identifier: `${zoomBotIdentifier.system}|${zoomBotIdentifier.value}`,
    });
    if (zoomBot?.id) {
      lines.push(`Found Zoom bot deployment: Bot/${zoomBot.id} (${zoomBotIdentifier.value}).`);
    } else {
      lines.push(
        `Could not find the env-specific Zoom bot "${zoomBotIdentifier.value}". Redeploy bots for this env with \`pnpm bots:deploy\`.`
      );
    }
  } catch (err) {
    lines.push(`Could not verify Bot deployment from this session: ${errMsg(err)}.`);
  }

  const scopedCriteria = `Appointment?status=booked&_compartment=Organization/${orgIdForTest}`;
  try {
    const scopedSubscription = await medplum.searchOne('Subscription', { criteria: scopedCriteria });
    const unscopedSubscription = scopedSubscription
      ? undefined
      : await medplum.searchOne('Subscription', { criteria: 'Appointment?status=booked' });

    if (scopedSubscription?.id || unscopedSubscription?.id) {
      const subscriptionId = scopedSubscription?.id ?? unscopedSubscription?.id;
      const criteria = scopedSubscription?.criteria ?? unscopedSubscription?.criteria;
      lines.push(`Found Subscription/${subscriptionId} with criteria "${criteria}".`);
    } else {
      lines.push(
        `Could not find the Zoom Subscription for booked appointments. Redeploy bots for this env with \`pnpm bots:deploy\`.`
      );
    }
  } catch (err) {
    lines.push(`Could not verify Subscription deployment from this session: ${errMsg(err)}.`);
  }

  lines.push(
    'Most likely remaining cause: missing Zoom Project secrets. These are not Vite app env vars. Set ZOOM_ACCOUNT_ID, ZOOM_CLIENT_ID, ZOOM_CLIENT_SECRET, and ZOOM_HOST_EMAIL in Medplum Admin under Project -> Secrets, then rerun the test.'
  );

  return lines.join(' ');
}

export interface CleanupCounts {
  Slot: number;
  Appointment: number;
  Encounter: number;
  ClinicalImpression: number;
  Task: number;
  Composition: number;
  QuestionnaireResponse: number;
  Observation: number;
  Condition: number;
  Consent: number;
  Coverage: number;
  Patient: number;
}

const CLEANUP_ORDER: (keyof CleanupCounts)[] = [
  'Slot',
  'Task',
  'ClinicalImpression',
  'Encounter',
  'Appointment',
  'Composition',
  'QuestionnaireResponse',
  'Observation',
  'Condition',
  'Consent',
  'Coverage',
  'Patient',
];

/**
 * Deletes every resource tagged with the test tag. The Patient gets cleaned up
 * last because Encounter/Appointment/etc reference it. Resources are deleted
 * sequentially per type to keep the error surface readable.
 */
export async function cleanupTaggedResources(medplum: MedplumClient, log: StepLogger): Promise<CleanupCounts> {
  const counts: CleanupCounts = {
    Slot: 0,
    Appointment: 0,
    Encounter: 0,
    ClinicalImpression: 0,
    Task: 0,
    Composition: 0,
    QuestionnaireResponse: 0,
    Observation: 0,
    Condition: 0,
    Consent: 0,
    Coverage: 0,
    Patient: 0,
  };

  for (const resourceType of CLEANUP_ORDER) {
    const found = await medplum.searchResources(resourceType, {
      _tag: `${TEST_TAG_SYSTEM}|${TEST_TAG_CODE}`,
      _count: '200',
    });
    if (found.length === 0) {
      continue;
    }
    log({ level: 'info', message: `Deleting ${found.length} ${resourceType}(s)...` });
    for (const r of found) {
      if (!r.id) {
        continue;
      }
      try {
        await medplum.deleteResource(resourceType, r.id);
        counts[resourceType] += 1;
      } catch (err) {
        log({
          level: 'error',
          message: `Failed to delete ${resourceType}/${r.id}: ${err instanceof Error ? err.message : String(err)}`,
        });
      }
    }
  }

  return counts;
}
